// Generazione report PDF tramite jsPDF + autotable.
// Il report &egrave; pensato per essere stampato e consegnato/inviato al medico.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PHASE_LABEL, PHASE_LABEL_SHORT } from './phases.js';

function fmtDateIT(iso) {
  if (!iso) return '';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function fmtDateShortIT(iso) {
  if (!iso) return '';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function intensityLabel(n) {
  if (n <= 3) return `${n}/10 (lieve)`;
  if (n <= 6) return `${n}/10 (medio)`;
  return `${n}/10 (forte)`;
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function isoDateOf(d) {
  return new Date(d).toISOString().slice(0, 10);
}

// Costruisce le statistiche aggregate sui dati nel range
export function buildSummary({ episodes, intakes, periods }) {
  const totalEpisodes = episodes.length;
  const avgIntensity = totalEpisodes
    ? +(episodes.reduce((s, e) => s + e.intensity, 0) / totalEpisodes).toFixed(2)
    : 0;
  const byPhase = {};
  const byType = {};
  const triggerCount = {};
  for (const e of episodes) {
    const p = e.cycle?.phase || 'unknown';
    byPhase[p] = (byPhase[p] || 0) + 1;
    byType[e.type] = (byType[e.type] || 0) + 1;
    for (const t of e.triggers || []) {
      const k = t.toLowerCase();
      triggerCount[k] = (triggerCount[k] || 0) + 1;
    }
  }
  const intakesByMed = {};
  for (const i of intakes) {
    const k = i.med?.name || 'Sconosciuta';
    intakesByMed[k] = (intakesByMed[k] || 0) + 1;
  }
  const sortedTriggers = Object.entries(triggerCount).sort((a, b) => b[1] - a[1]).slice(0, 8);

  return {
    totalEpisodes,
    avgIntensity,
    byPhase,
    byType,
    topTriggers: sortedTriggers,
    totalIntakes: intakes.length,
    intakesByMed,
    totalPeriods: periods.length,
  };
}

// Costruisce un elenco "diario giornaliero": per ogni giorno con attivit&agrave;,
// una riga narrativa pronta da stampare.
export function buildDiary({ from, to, periods, episodes, intakes }) {
  const map = new Map(); // dateStr -> { episodes:[], intakes:[], periodEvents:[] }
  function ensure(d) {
    if (!map.has(d)) map.set(d, { episodes: [], intakes: [], periodEvents: [] });
    return map.get(d);
  }

  // periodi: aggiungo evento "inizio" e "fine" se cadono nel range
  for (const p of periods) {
    if (p.startDate >= from && p.startDate <= to) {
      ensure(p.startDate).periodEvents.push({ kind: 'start', period: p });
    }
    if (p.endDate && p.endDate >= from && p.endDate <= to) {
      ensure(p.endDate).periodEvents.push({ kind: 'end', period: p });
    }
  }
  for (const e of episodes) {
    if (e.date >= from && e.date <= to) ensure(e.date).episodes.push(e);
  }
  for (const i of intakes) {
    if (i.date >= from && i.date <= to) ensure(i.date).intakes.push(i);
  }

  // costruisco lista ordinata cronologicamente
  const days = [...map.keys()].sort();
  const diary = days.map((d) => {
    const data = map.get(d);
    const parts = [];
    parts.push(capitalize(fmtDateIT(d)));
    if (data.periodEvents.length) {
      for (const ev of data.periodEvents) {
        if (ev.kind === 'start') {
          parts.push('Inizio ciclo mestruale.');
        } else {
          const days =
            (new Date(`${ev.period.endDate}T00:00:00`) -
              new Date(`${ev.period.startDate}T00:00:00`)) /
              86400000 +
            1;
          parts.push(`Fine ciclo mestruale (durata ${days} giorni).`);
        }
        if (ev.period.note) parts.push(`Note ciclo: ${ev.period.note}`);
      }
    }
    if (data.episodes.length) {
      const eps = data.episodes
        .map((e) => {
          const phase = PHASE_LABEL_SHORT[e.cycle?.phase];
          const trig = e.triggers?.length ? `; trigger: ${e.triggers.join(', ')}` : '';
          const note = e.note ? `; ${e.note}` : '';
          const day = e.cycle?.dayOfCycle ? `, giorno ${e.cycle.dayOfCycle} del ciclo` : '';
          const phLabel = phase && phase !== '—' ? ` (${phase}${day})` : '';
          return `${e.type} ${intensityLabel(e.intensity)}${phLabel}${trig}${note}`;
        })
        .join(' / ');
      parts.push(`Mal di testa: ${eps}.`);
    }
    if (data.intakes.length) {
      const intakesSorted = [...data.intakes].sort((a, b) =>
        a.takenAt < b.takenAt ? -1 : 1
      );
      const txt = intakesSorted
        .map((i) => {
          const med = i.med?.name || 'medicina';
          const dose = i.med?.dose ? ` ${i.med.dose}` : '';
          return `${med}${dose} alle ${fmtTime(i.takenAt)}`;
        })
        .join(', ');
      parts.push(`Assunzioni: ${txt}.`);
    }
    return { date: d, text: parts.join(' \u2014 ') };
  });
  return diary;
}

/**
 * Genera il PDF e lo restituisce come Blob.
 *
 * args:
 *   user: { name, email }
 *   from, to: 'YYYY-MM-DD'
 *   periods, episodes, intakes, cycleStats
 */
export function generateReportPDF(args) {
  const { user, from, to, periods, episodes, intakes, cycleStats } = args;
  const summary = buildSummary({ episodes, intakes, periods });
  const diary = buildDiary({ from, to, periods, episodes, intakes });

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  // Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Diario Mal di Testa', margin, y);
  y += 22;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(
    `Paziente: ${user?.name || ''}${user?.email ? `  (${user.email})` : ''}`,
    margin,
    y
  );
  y += 14;
  doc.text(
    `Periodo del report: ${fmtDateShortIT(from)} \u2013 ${fmtDateShortIT(to)}    Generato il: ${fmtDateShortIT(isoDateOf(new Date()))}`,
    margin,
    y
  );
  y += 18;

  // Riassunto
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Riassunto', margin, y);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  const summaryRows = [
    ['Episodi di mal di testa', String(summary.totalEpisodes)],
    ['Intensit\u00e0 media', summary.totalEpisodes ? `${summary.avgIntensity}/10` : '-'],
    ['Assunzioni di farmaci', String(summary.totalIntakes)],
    ['Periodi mestruali registrati', String(summary.totalPeriods)],
  ];
  if (cycleStats) {
    summaryRows.push(
      ['Lunghezza media ciclo', `${cycleStats.cycleLength || '-'} giorni`],
      ['Lunghezza media periodo', `${cycleStats.periodLength || '-'} giorni`],
      ['Ultimo ciclo', cycleStats.lastPeriodStart ? fmtDateShortIT(cycleStats.lastPeriodStart) : '-'],
      ['Prossimo ciclo previsto', cycleStats.nextPeriodEstimate ? fmtDateShortIT(cycleStats.nextPeriodEstimate) : '-'],
      ['Prossima ovulazione prevista', cycleStats.nextOvulationEstimate ? fmtDateShortIT(cycleStats.nextOvulationEstimate) : '-']
    );
  }

  autoTable(doc, {
    startY: y + 4,
    head: [['Metrica', 'Valore']],
    body: summaryRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9 },
    headStyles: { fillColor: [176, 48, 96] },
  });
  y = doc.lastAutoTable.finalY + 14;

  // Distribuzione mal di testa per fase del ciclo
  const phaseRows = Object.entries(summary.byPhase).map(([k, v]) => [
    PHASE_LABEL[k] || k,
    String(v),
  ]);
  if (phaseRows.length) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Distribuzione mal di testa per fase del ciclo', margin, y);
    autoTable(doc, {
      startY: y + 6,
      head: [['Fase', 'Episodi']],
      body: phaseRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [176, 48, 96] },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // Trigger pi\u00f9 ricorrenti
  if (summary.topTriggers.length) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Trigger pi\u00f9 ricorrenti', margin, y);
    autoTable(doc, {
      startY: y + 6,
      head: [['Trigger', 'Occorrenze']],
      body: summary.topTriggers.map(([k, v]) => [k, String(v)]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [176, 48, 96] },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // Tabella episodi mal di testa
  if (episodes.length) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Episodi di mal di testa', margin, y);
    autoTable(doc, {
      startY: y + 6,
      head: [['Data', 'Tipo', 'Intensit\u00e0', 'Fase ciclo', 'Trigger', 'Note']],
      body: episodes.map((e) => [
        fmtDateShortIT(e.date),
        e.type,
        `${e.intensity}/10`,
        PHASE_LABEL_SHORT[e.cycle?.phase] || '-',
        (e.triggers || []).join(', '),
        e.note || '',
      ]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [176, 48, 96] },
      columnStyles: {
        5: { cellWidth: 130 },
      },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // Tabella periodi
  if (periods.length) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Periodi mestruali nel range', margin, y);
    autoTable(doc, {
      startY: y + 6,
      head: [['Inizio', 'Fine', 'Durata (g)', 'Note']],
      body: periods.map((p) => {
        const dur = p.endDate
          ? Math.round(
              (new Date(`${p.endDate}T00:00:00`) - new Date(`${p.startDate}T00:00:00`)) /
                86400000 +
                1
            )
          : '-';
        return [fmtDateShortIT(p.startDate), p.endDate ? fmtDateShortIT(p.endDate) : '-', String(dur), p.note || ''];
      }),
      margin: { left: margin, right: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [176, 48, 96] },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // Tabella assunzioni medicine
  if (intakes.length) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Assunzioni di farmaci', margin, y);
    autoTable(doc, {
      startY: y + 6,
      head: [['Data', 'Ora', 'Medicina', 'Dose', 'Note']],
      body: intakes.map((i) => [
        fmtDateShortIT(i.date),
        fmtTime(i.takenAt),
        i.med?.name || '-',
        i.med?.dose || '',
        i.note || '',
      ]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [176, 48, 96] },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // Diario giornaliero narrativo
  if (diary.length) {
    if (y > 720) {
      doc.addPage();
      y = margin;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Diario giornaliero', margin, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const lineHeight = 13;
    for (const day of diary) {
      const lines = doc.splitTextToSize(day.text, pageW - margin * 2);
      const blockHeight = lines.length * lineHeight + 4;
      if (y + blockHeight > 800) {
        doc.addPage();
        y = margin;
      }
      doc.text(lines, margin, y);
      y += blockHeight;
    }
  }

  // Footer su ogni pagina
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      `Diario Mal di Testa \u2014 pagina ${p} di ${pageCount}`,
      margin,
      doc.internal.pageSize.getHeight() - 18
    );
    doc.setTextColor(0);
  }

  return doc;
}
